import React, { useState } from 'react';
import styled from 'styled-components';
import Button from './Button';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
  
  &:last-of-type {
    margin-bottom: var(--spacing-lg);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 500;
  color: var(--color-text);
`;

const InputBase = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-family: var(--font-main);
  font-size: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background-color: var(--color-background);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.2);
  }
  
  &::placeholder {
    color: #aaaaaa;
  }
  
  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
`;

const TextareaBase = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  font-family: var(--font-main);
  font-size: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  background-color: var(--color-background);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.2);
  }
  
  &::placeholder {
    color: #aaaaaa;
  }
  
  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.85rem;
  margin-top: var(--spacing-xs);
`;

const SuccessMessage = styled.div`
  color: #43a047;
  font-weight: 500;
  background-color: rgba(67, 160, 71, 0.1);
  padding: var(--spacing-md);
  border-radius: var(--border-radius-md);
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Form = ({ children, onSubmit, success, successMessage, fields, submitButtonText, ...props }) => {
  const [formData, setFormData] = useState({});
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(formData);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  return (
    <FormContainer onSubmit={handleSubmit} {...props}>
      {success && successMessage && (
        <SuccessMessage>{successMessage}</SuccessMessage>
      )}
      
      {fields ? (
        <>
          {fields.map((field, index) => {
            if (field.type === 'textarea') {
              return (
                <FormTextarea
                  key={field.name}
                  id={`form-${field.name}`}
                  name={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  required={field.required}
                />
              );
            }
            
            return (
              <FormInput
                key={field.name}
                id={`form-${field.name}`}
                name={field.name}
                type={field.type || 'text'}
                label={field.label}
                placeholder={field.placeholder}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.required}
              />
            );
          })}
          
          <FormButton
            type="submit"
            variant={'contained'}
            fullWidth
          >
            {submitButtonText || 'Отправить'}
          </FormButton>
        </>
      ) : (
        children
      )}
    </FormContainer>
  );
};

const FormInput = ({ 
  label, 
  id, 
  name,
  type = 'text',
  placeholder,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  ...props 
}) => {
  return (
    <FormGroup>
      {label && <Label htmlFor={id}>{label}{required && <span style={{ color: 'red' }}> *</span>}</Label>}
      <InputBase
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        {...props}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </FormGroup>
  );
};

const FormTextarea = ({ 
  label, 
  id, 
  name,
  placeholder,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  ...props 
}) => {
  return (
    <FormGroup>
      {label && <Label htmlFor={id}>{label}{required && <span style={{ color: 'red' }}> *</span>}</Label>}
      <TextareaBase
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        {...props}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </FormGroup>
  );
};

const FormButton = styled(Button)`
  margin-top: var(--spacing-md);
`;

export { Form, FormInput, FormTextarea, FormButton, FormGroup, FormRow };
export default Form; 